import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { useTrialStatus } from '../hooks/useTrialStatus';
import { useMarkPageVisited } from '../hooks/usePageVisit';
import TrialExpiredOverlay from '../components/TrialExpiredOverlay';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Star, StarOff, Plus, Search, Edit2, Trash2, ShoppingCart, Package, Loader2, X, Minus } from 'lucide-react';
import { toast } from 'sonner';
import { formatErrorMessage } from '../utils/errorFormatter';

const OrderTemplatesPage = () => {
  const { api } = useAuth();
  const { addToCart } = useCart();
  const { isTrialExpired } = useTrialStatus();
  const navigate = useNavigate();
  
  // Mark this page as visited (uses current pathname automatically)
  useMarkPageVisited();
  
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  // Suppliers and products
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  
  // Create/Edit dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [templateItems, setTemplateItems] = useState([]);
  
  // Product selection
  const [productSearch, setProductSearch] = useState('');
  const [currentStep, setCurrentStep] = useState(1); // 1: Détails, 2: Produits, 3: Résumé
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Saving state
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTemplates();
    fetchSuppliers();
  }, []);

  useEffect(() => {
    if (selectedSupplier) {
      fetchProducts(selectedSupplier);
    }
  }, [selectedSupplier]);

  const fetchSuppliers = async () => {
    try {
      const response = await api.get('/suppliers');
      setSuppliers(response.data);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    }
  };

  const fetchProducts = async (supplierId) => {
    try {
      const response = await api.get(`/products?supplier_id=${supplierId}`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/orders/favorites');
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Erreur lors du chargement des commandes récurrentes');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingTemplate(null);
    setTemplateName('');
    setTemplateDescription('');
    setSelectedSupplier('');
    setTemplateItems([]);
    setCurrentStep(1);
    setDialogOpen(true);
  };

  const openEditDialog = (template) => {
    setEditingTemplate(template);
    setTemplateName(template.name);
    setTemplateDescription(template.notes || '');
    setSelectedSupplier(template.supplier_id);
    setTemplateItems(template.items.map(item => ({
      product_id: item.product_id,
      quantity: item.quantity,
      article: item.article,
      code: item.code,
      unit_price: item.unit_price,
      unit: item.unit
    })));
    setCurrentStep(1);
    setDialogOpen(true);
  };

  const handleAddProduct = (product) => {
    const existing = templateItems.find(item => item.product_id === product.id);
    if (existing) {
      toast.error('Ce produit est déjà dans la commande récurrente');
      return;
    }
    
    setTemplateItems([...templateItems, {
      product_id: product.id,
      quantity: 1,
      article: product.article,
      code: product.code,
      unit_price: product.unit_price,
      unit: product.unit
    }]);
    setProductSearch('');
  };

  const handleUpdateQuantity = (idx, quantity) => {
    setTemplateItems(items => items.map((item, i) =>
      i === idx ? { ...item, quantity: Math.max(1, quantity) } : item
    ));
  };

  const handleRemoveProduct = (idx) => {
    setTemplateItems(items => items.filter((_, i) => i !== idx));
  };

  const calculateTotal = () => {
    return templateItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Le nom est obligatoire');
      return;
    }
    
    if (!selectedSupplier) {
      toast.error('Veuillez sélectionner un fournisseur');
      return;
    }
    
    if (templateItems.length === 0) {
      toast.error('Ajoutez au moins un produit à la commande récurrente');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: templateName,
        supplier_id: selectedSupplier,
        items: templateItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity
        })),
        notes: templateDescription
      };

      if (editingTemplate) {
        // For update, we need to delete and recreate (no PUT endpoint)
        await api.delete(`/orders/favorites/${editingTemplate.id}`);
        await api.post('/orders/favorites', payload);
        toast.success('Modèle mis à jour');
      } else {
        await api.post('/orders/favorites', payload);
        toast.success('Modèle créé');
      }
      await fetchTemplates();
      setDialogOpen(false);
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Erreur lors de la sauvegarde'));
    } finally {
      setSaving(false);
    }
  };

  const handleUseTemplate = async (template) => {
    try {
      // Add all products from template to cart
      let addedCount = 0;
      
      for (const item of template.items) {
        // Construct product object with all necessary fields for cart
        const product = {
          id: item.product_id,
          code: item.code,
          article: item.article,
          unit_price: item.unit_price,
          unit: item.unit,
          min_packaging: item.min_packaging || 1,
          supplier_id: template.supplier_id,
          supplier_name: template.supplier_name,
          supplier_delivery_days: template.supplier_delivery_days || [],
          supplier_lead_time_days: template.supplier_lead_time_days || 1,
          supplier_cutoff_hour: template.supplier_cutoff_hour || 12
        };
        
        addToCart(product, item.quantity);
        addedCount++;
      }
      
      toast.success(`${addedCount} produit${addedCount > 1 ? 's' : ''} ajouté${addedCount > 1 ? 's' : ''} au panier`, {
        description: `Modèle "${template.name}" chargé`,
        action: {
          label: 'Voir le panier',
          onClick: () => navigate('/cart')
        }
      });
      
      // Redirect to cart after a short delay
      setTimeout(() => navigate('/cart'), 1500);
      
    } catch (error) {
      console.error('Failed to load template into cart:', error);
      toast.error('Erreur lors du chargement de la commande récurrente');
    }
  };

  const handleDeleteClick = (template) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;
    
    setDeleting(true);
    try {
      await api.delete(`/orders/favorites/${templateToDelete.id}`);
      toast.success('Commande récurrente supprimée');
      await fetchTemplates();
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B4332]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in relative" data-testid="order-templates-page">
      {isTrialExpired && <TrialExpiredOverlay />}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Commandes récurrentes</h1>
          <p className="text-gray-500">Créez des commandes récurrentes pour vos commandes fréquentes</p>
          <p className="text-sm text-gray-600 mt-2 max-w-2xl">
            Enregistrez vos commandes habituelles comme modèles et réutilisez-les en un clic pour gagner du temps.
          </p>
        </div>
        <Button 
          onClick={openCreateDialog}
          className="bg-[#1B4332] hover:bg-[#0D2E1F] text-white"
          data-testid="order-templates-create-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle commande récurrente
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <Input
          placeholder="Rechercher une commande récurrente..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 h-12"
          data-testid="templates-search-input"
        />
      </div>

      {/* Templates List */}
      {filteredTemplates.length === 0 ? (
        <Card className="border border-gray-200">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 rounded-xl bg-[#D8F3DC] flex items-center justify-center mx-auto mb-4">
              <Star className="w-8 h-8 text-[#1B4332]" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune commande récurrente</h3>
            <p className="text-gray-500 mb-4">
              {search ? 'Aucune commande récurrente ne correspond à votre recherche' : 'Créez votre première commande récurrente pour gagner du temps sur vos commandes fréquentes'}
            </p>
            {!search && (
              <Button 
                onClick={openCreateDialog}
                className="bg-[#1B4332] hover:bg-[#0D2E1F] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Créer une commande récurrente
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredTemplates.map((template) => (
            <Card 
              key={template.id} 
              className="border-2 border-gray-200 hover:border-[#1B4332]/40 hover:shadow-lg transition-all flex flex-col"
              data-testid={`template-card-${template.id}`}
            >
              <CardHeader className="pb-4 bg-gradient-to-br from-gray-50 to-white border-b">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Star className="w-5 h-5 text-amber-500 fill-amber-500 flex-shrink-0" />
                      <CardTitle className="text-xl font-bold text-gray-900 truncate">
                        {template.name}
                      </CardTitle>
                    </div>
                    {template.description && (
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {template.description}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Fournisseur */}
                <div className="mt-3 flex items-center gap-2 text-sm">
                  <Badge variant="outline" className="bg-white border-gray-300">
                    <Package className="w-3 h-3 mr-1" />
                    {template.supplier_name}
                  </Badge>
                  <span className="text-gray-400">•</span>
                  <span className="text-gray-600 font-medium">
                    {template.items?.length || 0} produit{(template.items?.length || 0) > 1 ? 's' : ''}
                  </span>
                </div>
              </CardHeader>
              
              <CardContent className="pt-4 space-y-4 flex-1 flex flex-col">
                {/* Liste des produits (aperçu) */}
                <div className="space-y-2 flex-1">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Contenu de la commande
                  </p>
                  <div className="space-y-1.5 max-h-32 overflow-y-auto">
                    {template.items && template.items.length > 0 ? (
                      template.items.slice(0, 5).map((item, idx) => (
                        <div 
                          key={idx} 
                          className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">
                              {item.article || item.product_name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {item.code} • Qté: {item.quantity}
                            </p>
                          </div>
                          <p className="text-sm font-semibold text-gray-700 ml-2 tabular-nums">
                            {(item.unit_price * item.quantity).toFixed(2)} €
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-400 py-2">Aucun produit</p>
                    )}
                    {template.items && template.items.length > 5 && (
                      <p className="text-xs text-gray-500 pt-1 text-center">
                        + {template.items.length - 5} autre{template.items.length - 5 > 1 ? 's' : ''} produit{template.items.length - 5 > 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Coût total */}
                <div className="space-y-4 mt-auto">
                  {template.total_amount && (
                    <div className="p-3 bg-gradient-to-r from-[#D8F3DC] to-[#E8F7EC] rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Coût estimé</span>
                        <span className="text-2xl font-bold text-[#1B4332] tabular-nums">
                          {template.total_amount.toFixed(2)} €
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => handleUseTemplate(template)}
                      className="flex-1 bg-[#1B4332] hover:bg-[#0D2E1F] text-white h-10"
                      data-testid="order-template-use-button"
                    >
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      Utiliser cette commande
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => openEditDialog(template)}
                      className="h-10 w-10"
                      data-testid="order-template-rename-button"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleDeleteClick(template)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 h-10 w-10"
                      data-testid="order-template-delete-button"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog with Steps */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="order-template-dialog">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="text-2xl">
              {editingTemplate ? 'Modifier la commande récurrente' : 'Nouvelle commande récurrente'}
            </DialogTitle>
            <DialogDescription>
              {editingTemplate ? 'Modifiez votre commande récurrente' : 'Créez une commande récurrente réutilisable en 3 étapes'}
            </DialogDescription>
            
            {/* Steps indicator */}
            <div className="flex items-center gap-2 pt-4">
              {[
                { num: 1, label: 'Détails' },
                { num: 2, label: 'Produits' },
                { num: 3, label: 'Résumé' }
              ].map((step, idx) => (
                <React.Fragment key={step.num}>
                  <div className="flex items-center gap-2">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full font-semibold text-sm transition-colors ${
                      currentStep === step.num 
                        ? 'bg-[#1B4332] text-white' 
                        : currentStep > step.num
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}>
                      {currentStep > step.num ? '✓' : step.num}
                    </div>
                    <span className={`text-sm font-medium ${
                      currentStep >= step.num ? 'text-gray-900' : 'text-gray-400'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                  {idx < 2 && (
                    <div className={`flex-1 h-0.5 ${
                      currentStep > step.num ? 'bg-green-500' : 'bg-gray-200'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-6">
            {/* Step 1: Details */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="template-name" className="text-base font-semibold">
                    Nom de la commande récurrente <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="template-name"
                    placeholder="Ex: Commande hebdomadaire produits frais"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    className="h-11"
                    data-testid="order-template-name-input"
                  />
                  <p className="text-xs text-gray-500">Donnez un nom clair et descriptif à votre commande récurrente</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="template-supplier" className="text-base font-semibold">
                    Fournisseur <span className="text-red-500">*</span>
                  </Label>
                  <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
                    <SelectTrigger className="h-11" data-testid="order-template-supplier-select">
                      <SelectValue placeholder="Sélectionner un fournisseur" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map(supplier => (
                        <SelectItem key={supplier.id} value={supplier.id}>
                          <div className="flex items-center gap-2">
                            <Package className="w-4 h-4 text-gray-400" />
                            {supplier.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">Choisissez le fournisseur pour cette commande récurrente</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="template-description" className="text-base font-semibold">
                    Notes (optionnel)
                  </Label>
                  <Textarea
                    id="template-description"
                    placeholder="Notes, remarques, ou instructions spécifiques..."
                    value={templateDescription}
                    onChange={(e) => setTemplateDescription(e.target.value)}
                    rows={4}
                    data-testid="order-template-description-textarea"
                  />
                  <p className="text-xs text-gray-500">Ajoutez des informations utiles pour cette commande récurrente</p>
                </div>
              </div>
            )}
            
            {/* Step 2: Products */}
            {currentStep === 2 && (
              <div className="space-y-4">
                {!selectedSupplier ? (
                  <div className="text-center py-12 text-gray-500">
                    <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium">Sélectionnez d'abord un fournisseur</p>
                    <p className="text-sm mt-1">Retournez à l'étape précédente</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    {/* Left: Available products */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-lg">Produits disponibles</h3>
                        <Badge variant="outline">{products.length} produits</Badge>
                      </div>
                      
                      {/* Search */}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <Input
                          placeholder="Rechercher un produit..."
                          value={productSearch}
                          onChange={(e) => setProductSearch(e.target.value)}
                          className="pl-9"
                        />
                      </div>
                      
                      {/* Products list */}
                      <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
                        {products
                          .filter(p => 
                            !productSearch || 
                            p.article.toLowerCase().includes(productSearch.toLowerCase()) ||
                            p.code.toLowerCase().includes(productSearch.toLowerCase())
                          )
                          .map(product => {
                            const isAdded = templateItems.some(item => item.product_id === product.id);
                            return (
                              <div
                                key={product.id}
                                className={`p-3 border-b border-gray-100 last:border-0 transition-colors ${
                                  isAdded ? 'bg-green-50' : 'hover:bg-gray-50 cursor-pointer'
                                }`}
                                onClick={() => !isAdded && handleAddProduct(product)}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm text-gray-900 truncate">
                                      {product.article}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {product.code} • {product.unit_price.toFixed(2)} € / {product.unit}
                                    </p>
                                  </div>
                                  {isAdded ? (
                                    <Badge className="bg-green-500 text-white flex-shrink-0">
                                      <Check className="w-3 h-3 mr-1" />
                                      Ajouté
                                    </Badge>
                                  ) : (
                                    <Plus className="w-5 h-5 text-[#1B4332] flex-shrink-0" />
                                  )}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                    
                    {/* Right: Selected products */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-lg">Produits sélectionnés</h3>
                        <Badge variant="outline" className="bg-[#D8F3DC]">
                          {templateItems.length} produit{templateItems.length > 1 ? 's' : ''}
                        </Badge>
                      </div>
                      
                      {templateItems.length === 0 ? (
                        <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
                          <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <p className="text-sm text-gray-500 font-medium">Aucun produit sélectionné</p>
                          <p className="text-xs text-gray-400 mt-1">Cliquez sur un produit pour l'ajouter</p>
                        </div>
                      ) : (
                        <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto divide-y divide-gray-100">
                          {templateItems.map((item, idx) => (
                            <div key={idx} className="p-3 hover:bg-gray-50">
                              <div className="flex items-start gap-3">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm text-gray-900 truncate">
                                    {item.article}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    {item.code} • {item.unit_price.toFixed(2)} €
                                  </p>
                                </div>
                                
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => handleUpdateQuantity(idx, Math.max(1, item.quantity - 1))}
                                  >
                                    <Minus className="w-3 h-3" />
                                  </Button>
                                  <Input
                                    type="number"
                                    min="1"
                                    value={item.quantity}
                                    onChange={(e) => handleUpdateQuantity(idx, parseInt(e.target.value) || 1)}
                                    className="w-16 h-7 text-center"
                                  />
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => handleUpdateQuantity(idx, item.quantity + 1)}
                                  >
                                    <Plus className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => handleRemoveProduct(idx)}
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                              <div className="mt-2 flex items-center justify-between text-xs">
                                <span className="text-gray-500">Total ligne</span>
                                <span className="font-semibold text-gray-900 tabular-nums">
                                  {(item.unit_price * item.quantity).toFixed(2)} €
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Step 3: Summary */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-[#D8F3DC] to-[#E8F7EC] rounded-xl p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Résumé de la commande</h3>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Nom</p>
                      <p className="font-semibold text-gray-900">{templateName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Fournisseur</p>
                      <p className="font-semibold text-gray-900">
                        {suppliers.find(s => s.id === selectedSupplier)?.name}
                      </p>
                    </div>
                  </div>
                  
                  {templateDescription && (
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-1">Notes</p>
                      <p className="text-sm text-gray-700">{templateDescription}</p>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between pt-4 border-t border-green-200">
                    <span className="text-base font-medium text-gray-700">Coût total estimé</span>
                    <span className="text-3xl font-bold text-[#1B4332] tabular-nums">
                      {calculateTotal().toFixed(2)} €
                    </span>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-lg mb-3">
                    Produits ({templateItems.length})
                  </h4>
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-64 overflow-y-auto">
                    {templateItems.map((item, idx) => (
                      <div key={idx} className="p-3 flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-sm text-gray-900">{item.article}</p>
                          <p className="text-xs text-gray-500">{item.code}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">
                            Qté: {item.quantity}
                          </p>
                          <p className="text-xs text-gray-500 tabular-nums">
                            {(item.unit_price * item.quantity).toFixed(2)} €
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="border-t pt-4 flex items-center justify-between">
            <div>
              {currentStep > 1 && (
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(currentStep - 1)}
                >
                  Précédent
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {currentStep < 3 ? (
                <Button
                  onClick={() => {
                    if (currentStep === 1 && (!templateName || !selectedSupplier)) {
                      toast.error('Veuillez remplir tous les champs obligatoires');
                      return;
                    }
                    if (currentStep === 2 && templateItems.length === 0) {
                      toast.error('Ajoutez au moins un produit');
                      return;
                    }
                    setCurrentStep(currentStep + 1);
                  }}
                  className="bg-[#1B4332] hover:bg-[#0D2E1F] text-white"
                >
                  Suivant
                </Button>
              ) : (
                <Button
                  onClick={handleSaveTemplate}
                  disabled={saving}
                  className="bg-[#1B4332] hover:bg-[#0D2E1F] text-white"
                  data-testid="order-template-save-button"
                >
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingTemplate ? 'Enregistrer les modifications' : 'Créer la commande récurrente'}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette commande récurrente ?</AlertDialogTitle>
            <AlertDialogDescription>
              Vous êtes sur le point de supprimer la commande récurrente <strong>"{templateToDelete?.name}"</strong>.
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OrderTemplatesPage;